import {
  type FilamentPurchaseBody,
  PURCHASE_PAYMENT_LABELS,
  Purchase,
  PurchasePayment,
  type StoredFile,
  formatBRL,
  formatIsoDate,
  isoDateInSaoPaulo,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { AffixInput, Field, SwitchRow } from '../../components/Form';
import { Combobox } from '../../components/Combobox';
import { filamentOption } from '../../components/pickers';
import { PageHead } from '../../components/PageHead';
import { Alert } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, newIdempotencyKey } from '../../lib/api';
import { useFilaments } from '../../lib/catalog-api';
import { uploadFile } from '../../lib/files-api';
import { useInvalidateInventory } from '../../lib/inventory-api';
import { errorMessage } from '../../lib/messages';
import { centavosInput, parseBrDate, parseCentavos } from '../../lib/numbers';

interface Line {
  key: string;
  filamentId: string;
  spools: string;
  price: string;
}

const newLine = (filamentId: string, price: number | null): Line => ({
  key: crypto.randomUUID(),
  filamentId,
  spools: '1',
  price: price !== null ? centavosInput(price) : '',
});

/** "Registrar compra" of filament spools (board Compra-Carreteis). */
export function PurchasePage() {
  const filaments = useFilaments();
  const toast = useToast();
  const invalidate = useInvalidateInventory();
  const fileInput = useRef<HTMLInputElement>(null);
  const list = filaments.data?.data ?? [];
  const [idem, setIdem] = useState(newIdempotencyKey);
  const [supplier, setSupplier] = useState('Loja oficial Bambu Lab');
  const [date, setDate] = useState(formatIsoDate(isoDateInSaoPaulo()));
  const [payment, setPayment] = useState<PurchasePayment>('pix');
  const [freight, setFreight] = useState('0,00');
  const [lines, setLines] = useState<Line[]>([]);
  const [receipts, setReceipts] = useState<StoredFile[]>([]);
  const [tried, setTried] = useState(false);
  const [done, setDone] = useState<Purchase | null>(null);
  const [seeded, setSeeded] = useState(false);
  // Start with one line for the first filament once the catalog arrives.
  if (!seeded && list[0]) {
    setSeeded(true);
    setLines([
      newLine(list[0].id, Math.round((list[0].pricePerKgCentavos * list[0].netWeightGrams) / 1000)),
    ]);
  }

  const parsed = lines.map((l) => {
    const f = list.find((x) => x.id === l.filamentId);
    const spools = /^\d+$/.test(l.spools.trim()) ? Number(l.spools) : null;
    const price = parseCentavos(l.price);
    return { line: l, f, spools, price, grams: (spools ?? 0) * (f?.netWeightGrams ?? 1000) };
  });
  const freightC = parseCentavos(freight) ?? 0;
  const totalGrams = parsed.reduce((n, p) => n + p.grams, 0);
  const goods = parsed.reduce((n, p) => n + (p.spools ?? 0) * (p.price ?? 0), 0);
  const spoolCount = parsed.reduce((n, p) => n + (p.spools ?? 0), 0);
  const iso = parseBrDate(date);
  const badLine = (p: (typeof parsed)[number]) =>
    !p.f || !p.spools || p.spools < 1 || !p.price || p.price <= 0;
  const valid = iso !== null && parsed.length > 0 && !parsed.some(badLine);

  // "Etiquetas · Imprimir ao salvar": the labels tab opens on the click (so the browser allows it)
  // and goes to the printable page once the spools exist.
  const [printLabels, setPrintLabels] = useState(true);
  const labelsTab = useRef<Window | null>(null);
  const [printed, setPrinted] = useState(false);
  const save = useMutation({
    mutationFn: () => {
      const body: FilamentPurchaseBody = {
        supplier: supplier.trim() || null,
        purchasedOn: iso!,
        paymentMethod: payment,
        freightCentavos: freightC,
        fileIds: receipts.map((r) => r.id),
        lines: parsed.map((p) => ({
          filamentId: p.f!.id,
          spools: p.spools!,
          pricePerSpoolCentavos: p.price!,
        })),
      };
      return api.post('/filament-purchases', Purchase, body, { idempotencyKey: idem });
    },
    onError: () => {
      labelsTab.current?.close();
      labelsTab.current = null;
    },
    onSuccess: (p) => {
      invalidate();
      setDone(p);
      if (labelsTab.current) {
        labelsTab.current.location.href = `/carreteis/etiquetas?ids=${p.spools.map((s) => s.id).join(',')}&imprimir=1`;
        labelsTab.current = null;
        setPrinted(true);
      } else setPrinted(false);
      toast(
        `Compra salva. ${p.spools.length} ${p.spools.length === 1 ? 'carretel criado' : 'carretéis criados'}.`,
      );
    },
  });
  const attach = useMutation({
    mutationFn: (file: File) => uploadFile(file, 'document'),
    onSuccess: (f) => {
      setReceipts((r) => [...r, f]);
      toast(`${f.name} anexado.`);
    },
    onError: (err) => toast(errorMessage(err)),
  });

  if (done) {
    const first = done.spools[0]?.code;
    const last = done.spools.at(-1)?.code;
    return (
      <>
        <PageHead crumb="Filamentos e carretéis" title="Registrar compra" />
        <div className="card col" style={{ gap: 12, maxWidth: 640, borderColor: 'var(--ok)' }}>
          <span className="badge ok" style={{ alignSelf: 'flex-start' }}>
            Compra registrada
          </span>
          <p className="sm muted">
            {done.supplier ?? 'Sem fornecedor'} · {formatIsoDate(done.purchasedOn)}
            {done.paymentMethod ? ` · ${PURCHASE_PAYMENT_LABELS[done.paymentMethod]}` : ''}
          </p>
          <div className="kv">
            <span>
              {done.spools.length}{' '}
              {done.spools.length === 1 ? 'carretel criado' : 'carretéis criados'}, lacrados
            </span>
            <span className="v">{first === last ? first : `${first} a ${last}`}</span>
          </div>
          <div className="kv">
            <span>Preço de reposição atualizado</span>
            <span className="v">
              {done.lines.length} {done.lines.length === 1 ? 'filamento' : 'filamentos'}
            </span>
          </div>
          <div className="kv">
            <span>Total com frete</span>
            <span className="v">{formatBRL(done.totalCentavos)}</span>
          </div>
          <div className="kv sm">
            <span>Despesa lançada em Filamento</span>
            <span className="v">{formatBRL(done.totalCentavos)}</span>
          </div>
          <div className="kv sm">
            <span>Etiquetas</span>
            <span className="v">
              {printed ? `${done.spools.length} enviadas para a impressora` : 'não impressas'}
            </span>
          </div>
          <div className="row wrap" style={{ gap: 6 }}>
            {done.spools.map((s) => (
              <span key={s.id} className="badge">
                {s.code}
              </span>
            ))}
          </div>
          <div className="row wrap" style={{ gap: 10 }}>
            <Link
              className="btn p"
              to={`/carreteis/etiquetas?ids=${done.spools.map((s) => s.id).join(',')}`}
            >
              Imprimir etiquetas
            </Link>
            <Link className="btn" to="/despesas">
              Ver despesa
            </Link>
            <Link className="btn" to="/filamentos/carreteis">
              Ver carretéis
            </Link>
            <button
              type="button"
              className="btn g"
              onClick={() => {
                setDone(null);
                setTried(false);
                setSeeded(false);
                setLines([]);
                setReceipts([]);
                setFreight('0,00');
                setIdem(newIdempotencyKey());
              }}
            >
              Registrar outra compra
            </button>
          </div>
        </div>
      </>
    );
  }

  const cols = 'minmax(190px, 2fr) 76px 80px 120px 112px 36px';
  return (
    <>
      <PageHead crumb="Filamentos e carretéis" title="Registrar compra" />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
        <div className="col" style={{ flex: '3 1 560px', minWidth: 0, gap: 18 }}>
          <section className="card">
            <div
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(170px, 100%), 1fr))',
              }}
            >
              <Field id="pc-sup" label="Fornecedor">
                <input
                  id="pc-sup"
                  className="input"
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                />
              </Field>
              <Field id="pc-date" label="Data" error={tried && !iso ? 'Use DD/MM/AAAA.' : null}>
                <input
                  id="pc-date"
                  className="input"
                  inputMode="numeric"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </Field>
              <Field id="pc-pay" label="Pagamento">
                <select
                  id="pc-pay"
                  className="input"
                  value={payment}
                  onChange={(e) => setPayment(PurchasePayment.parse(e.target.value))}
                >
                  {PurchasePayment.options.map((p) => (
                    <option key={p} value={p}>
                      {PURCHASE_PAYMENT_LABELS[p]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field id="pc-fr" label="Frete">
                <AffixInput id="pc-fr" prefix="R$" value={freight} onChange={setFreight} />
              </Field>
            </div>
          </section>
          <section className="card flat">
            <div className="row between" style={{ padding: '16px 18px 6px' }}>
              <h2>Itens</h2>
              <span className="xs muted">frete rateado pelo peso líquido</span>
            </div>
            <div className="gt" style={{ overflowX: 'auto' }}>
              <div className="gtr head" style={{ gridTemplateColumns: cols, minWidth: 680 }}>
                <span>Filamento</span>
                <span className="num">Carretéis</span>
                <span className="num">Peso líquido</span>
                <span className="num">Preço por carretel</span>
                <span className="num">R$/kg com frete</span>
                <span />
              </div>
              {parsed.map((p) => {
                const perKg =
                  p.spools && p.price && p.f && totalGrams > 0
                    ? Math.round(
                        ((p.spools * p.price + (freightC * p.grams) / totalGrams) * 1000) / p.grams,
                      )
                    : null;
                const set = (patch: Partial<Line>) =>
                  setLines(lines.map((l) => (l.key === p.line.key ? { ...l, ...patch } : l)));
                return (
                  <div
                    key={p.line.key}
                    className="gtr"
                    style={{ gridTemplateColumns: cols, minWidth: 680 }}
                  >
                    <Combobox
                      ariaLabel="Filamento"
                      value={p.line.filamentId}
                      options={list.map(filamentOption)}
                      onChange={(id) => {
                        const f = list.find((x) => x.id === id);
                        set({
                          filamentId: id,
                          price: f
                            ? centavosInput(
                                Math.round((f.pricePerKgCentavos * f.netWeightGrams) / 1000),
                              )
                            : p.line.price,
                        });
                      }}
                    />
                    <input
                      className="input"
                      aria-label="Carretéis"
                      inputMode="numeric"
                      style={{ textAlign: 'right' }}
                      aria-invalid={tried && (!p.spools || p.spools < 1)}
                      value={p.line.spools}
                      onChange={(e) => set({ spools: e.target.value })}
                    />
                    <span className="num">
                      {p.grams ? `${(p.grams / 1000).toLocaleString('pt-BR')} kg` : '—'}
                    </span>
                    <AffixInput
                      ariaLabel="Preço por carretel"
                      prefix="R$"
                      value={p.line.price}
                      invalid={tried && (!p.price || p.price <= 0)}
                      onChange={(v) => set({ price: v })}
                    />
                    <span className="num">
                      {perKg !== null ? (
                        <>
                          <span
                            style={{
                              display: 'block',
                              color:
                                perKg <= (p.f?.pricePerKgCentavos ?? 0)
                                  ? 'var(--ok)'
                                  : 'var(--err)',
                            }}
                          >
                            {formatBRL(perKg)}
                          </span>
                          <span className="xs muted">
                            antes {formatBRL(p.f!.pricePerKgCentavos)}
                          </span>
                        </>
                      ) : (
                        '—'
                      )}
                    </span>
                    <button
                      type="button"
                      className="btn sm g icon"
                      aria-label="Remover item"
                      onClick={() => setLines(lines.filter((l) => l.key !== p.line.key))}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              {parsed.length === 0 ? (
                <div className="gtr" style={{ gridTemplateColumns: '1fr' }}>
                  <span className="sm muted">Nenhum item. Adicione pelo menos um filamento.</span>
                </div>
              ) : (
                <div className="gtr foot" style={{ gridTemplateColumns: cols, minWidth: 680 }}>
                  <span>Total com frete</span>
                  <span className="num">{spoolCount}</span>
                  <span className="num">{(totalGrams / 1000).toLocaleString('pt-BR')} kg</span>
                  <span />
                  <span className="num">{formatBRL(goods + freightC)}</span>
                  <span />
                </div>
              )}
            </div>
            <div style={{ padding: '10px 18px 16px' }}>
              <button
                type="button"
                className="btn sm"
                disabled={list.length === 0}
                onClick={() => {
                  const f = list[0]!;
                  setLines([
                    ...lines,
                    newLine(f.id, Math.round((f.pricePerKgCentavos * f.netWeightGrams) / 1000)),
                  ]);
                }}
              >
                + Item
              </button>
            </div>
          </section>
        </div>
        <aside
          className="card col"
          style={{ flex: '1 1 280px', gap: 10, position: 'sticky', top: 16 }}
        >
          <h2>Ao salvar</h2>
          <p className="sm">
            Cria{' '}
            <b>
              {spoolCount} {spoolCount === 1 ? 'carretel lacrado' : 'carretéis lacrados'}
            </b>{' '}
            com código e etiqueta.
          </p>
          <p className="sm">
            Atualiza o preço de reposição de {new Set(parsed.map((p) => p.line.filamentId)).size}{' '}
            {new Set(parsed.map((p) => p.line.filamentId)).size === 1 ? 'filamento' : 'filamentos'}{' '}
            para o R$/kg com frete.
          </p>
          <p className="sm muted">
            Lança a despesa <b>{formatBRL(goods + freightC)}</b> em Filamento.
          </p>
          <span className="lbl">Nota ou comprovante</span>
          <div className="row wrap" style={{ gap: 6 }}>
            {receipts.map((r) => (
              <span key={r.id} className="badge">
                {r.name}
              </span>
            ))}
          </div>
          <input
            ref={fileInput}
            type="file"
            hidden
            accept=".pdf,image/*,.xml"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) attach.mutate(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            className="btn sm"
            disabled={attach.isPending}
            onClick={() => fileInput.current?.click()}
          >
            {attach.isPending ? 'Enviando…' : 'Anexar arquivo'}
          </button>
          <SwitchRow
            on={printLabels}
            onChange={setPrintLabels}
            label="Etiquetas · Imprimir ao salvar"
          />
          {tried && !valid ? (
            <Alert tone="err">
              {parsed.length === 0
                ? 'Adicione pelo menos um item.'
                : 'Confira a data, as quantidades (inteiras) e os preços marcados.'}
            </Alert>
          ) : null}
          {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
          <button
            type="button"
            className="btn p"
            disabled={save.isPending}
            onClick={() => {
              setTried(true);
              if (!valid) return;
              if (printLabels) labelsTab.current = window.open('about:blank', '_blank');
              save.mutate();
            }}
          >
            Salvar compra
          </button>
        </aside>
      </div>
    </>
  );
}
